import React, {PropTypes} from 'react';
import cx from 'classnames';
import {get, isEmpty, find} from 'lodash';
import {YesNoItems} from '../../../lib/dropdowns';
import CustomTextField from '../../../common/components/TextField';
import ToggleButton from '../../../common/components/c_ToggleButton';
import DropDownMenu from '../../../common/components/c_DropDownMenu';
import DateInput from '../../../common/components/DateInput';
import RaisedButton from '../../../common/components/c_RaisedButton';
import SubText from '../../../common/components/SubText';
import InfoModal from '../../../common/components/f_InfoModal';

const YesNo = YesNoItems();

function renderAttributes(survey, property, labels, errors = {}) {
  return {
    key: `${property}`,
    floatingLabelFixed: true,
    floatingLabelText: labels[property],
    value: survey[property],
    errorText: get(errors, `${property}[0]`)
  };
}

function renderTextAttributes(survey, property, labels, errors = {}, onChange) {
  const attributes = renderAttributes(survey, property, labels, errors);
  return {
    ...attributes,
    onChange: onChange(property),
    onBlur: onChange(property),
    value: survey[property] || '' // REACT: To prevent controlled components from switching to uncontrolled
  };
}

function renderMenuAttributes(survey, property, menuItems, labels, errors) {
  const attributes = renderAttributes(survey, property, labels, errors);
  attributes.menuItems = menuItems;
  return attributes;
}

function renderModalAttributes(property, labels, onClick) {
  return {
    text: labels[property],
    note: labels[`${property}Message`],
    onClick
  };
}

function renderModal(message = null, onClose) {
  const close = () => onClose();

  if (message) {
    return (
      <InfoModal
        cancelFunc={close}
        buttonLabel='close'
        transitionFunc={close}
        text={message}
        title='Note'
      />
    );
  }
}

function renderTitleText(labels) {
  const title = get(labels, 'titleText');
  if (title) {
    return (
      <div className='form-heading' data-id='Technology Survey'>
        {title}
      </div>
    );
  }
}

function renderInstructionText({fieldLabels = {}, survey = {}} = {}) {
  const text = get(fieldLabels, 'instructionText');
  if (text && !survey.dateCompleted) {
    return (
      <div className='form-text example-text pre'>
        {text}
      </div>
    );
  }
}

function renderErrorText(errors) {
  if (!isEmpty(errors)) {
    return (
      <div className={'left-container error-message'}>Missing or invalid fields.</div>
    );
  }
}

function renderCompleteButton({allowSubmit = false, survey = {}} = {}, onSubmit = () => false) {

  if (!survey.dateCompleted) {
    return (
      <RaisedButton
        label={'Submit'}
        data-id={'Complete Button'}
        onClick={onSubmit}
        disabled={!allowSubmit}
      />
    );
  }

}

function ComputerSection(props) {

  const {
    survey,
    errors,
    onChange,
    onModal,
    fieldLabels,
    staticData: {
      computerTypes,
      operatingSystemTypes,
      windowsOperatingSystems,
      computerMemorySizes
    }
  } = props;

  const osWindows = find(operatingSystemTypes, { hardcodelink: 'PC' });

  return (

    <div className='grid form-contents large-floating-text'>
      <div className='row'>
        <div className='col'>
          <DropDownMenu
            canAutoPosition='true'
            className='internet-type'
            onChange={onChange('computerTypeId')}
            {...renderMenuAttributes(survey, 'computerTypeId', computerTypes, fieldLabels, errors)}
          />
        </div>

        <div className='col'>
          <ToggleButton
            onChange={onChange('computerOperatingSystemId')}
            {...renderMenuAttributes(survey, 'computerOperatingSystemId', operatingSystemTypes, fieldLabels, errors)}
          />
        </div>

        <div className='col'>
          <CustomTextField
            {...renderTextAttributes(survey, 'computerMakeAndModel', fieldLabels, errors, onChange)}
          />
        </div>

      </div>
      <div className='row'>

        { survey.computerOperatingSystemId === get(osWindows, 'id') &&
          <div className='col'>
            <SubText
              {...renderModalAttributes('windowsOSModal', fieldLabels, onModal)}
            >

              <DropDownMenu
                onChange={onChange('windowsOperatingSystemId')}
                {...renderMenuAttributes(survey, 'windowsOperatingSystemId', windowsOperatingSystems, fieldLabels, errors)}
              />
            </SubText>
          </div>
        }
        <div className='col'>
          <SubText
            {...renderModalAttributes('computerMemorySizes', fieldLabels, onModal)}
          >
            <DropDownMenu
              onChange={onChange('computerMemorySizeId')}
              {...renderMenuAttributes(survey, 'computerMemorySizeId', computerMemorySizes, fieldLabels, errors)}
            />
          </SubText>
        </div>

      </div>

    </div>

  );
}

ComputerSection.propTypes = {
  onChange: PropTypes.func.isRequired,
  onModal: PropTypes.func,
  survey: PropTypes.object.isRequired,
  staticData: PropTypes.object.isRequired,
  fieldLabels: PropTypes.object.isRequired,
  errors: PropTypes.object
};

function EquipmentSection(props) {

  const {
    survey,
    errors,
    onChange,
    fieldLabels,
    staticData: {
      displayConnectionTypes
    }
  } = props;

  return (

    <div className='grid form-contents large-floating-text'>

      <div className='row'>
        <div className='col'>
          <ToggleButton
            onChange={onChange('hasHeadsetWithMicrophone')}
            {...renderMenuAttributes(survey, 'hasHeadsetWithMicrophone', YesNo, fieldLabels, errors)}
          />
        </div>
        <div className='col'>
          <ToggleButton
            onChange={onChange('hasSpeakersWithMicrophone')}
            {...renderMenuAttributes(survey, 'hasSpeakersWithMicrophone', YesNo, fieldLabels, errors)}
          />
        </div>
        <div className='col'>
          <ToggleButton
            onChange={onChange('hasWebcam')}
            {...renderMenuAttributes(survey, 'hasWebcam', YesNo, fieldLabels, errors)}
          />
        </div>

      </div>

      <div className='row'>
        <div className='col'>
          <ToggleButton
            onChange={onChange('hasSecondMonitor')}
            {...renderMenuAttributes(survey, 'hasSecondMonitor', YesNo, fieldLabels, errors)}
          />
        </div>
      </div>
    </div>

  );

}

EquipmentSection.propTypes = {
  onChange: PropTypes.func.isRequired,
  survey: PropTypes.object.isRequired,
  staticData: PropTypes.object.isRequired,
  fieldLabels: PropTypes.object.isRequired,
  errors: PropTypes.object
};

function SoftwareSection(props) {

  const {
    survey,
    errors,
    onChange,
    onModal,
    fieldLabels,
    staticData: {
      msOfficeVersions
    }
  } = props;

  return (
    <div className='grid form-contents large-floating-text'>
      <div className='row'>
        <div className='col'>
          <CustomTextField
            disabled={false}
            {...renderTextAttributes(survey, 'antiVirusName', fieldLabels, errors, onChange)}
          />
        </div>
        <div className='col'>
          <DateInput
            disabled={false}
            defaultValue={!survey.hasAntiVirusInstalled ? '' : survey.antiVirusExpired}
            autoOk
            dataId={'antiVirusExpired'}
            minDate={new Date()}
            {...renderTextAttributes(survey, 'antiVirusExpired', fieldLabels, errors, onChange)}
          />
        </div>
        <div className='col'>
        </div>
      </div>
    </div>
  );
}

SoftwareSection.propTypes = {
  onChange: PropTypes.func.isRequired,
  onModal: PropTypes.func,
  survey: PropTypes.object.isRequired,
  staticData: PropTypes.object.isRequired,
  fieldLabels: PropTypes.object.isRequired,
  errors: PropTypes.object
};

function InternetSection(props) {

  const {
    survey,
    errors,
    onChange,
    onModal,
    fieldLabels,
    staticData: {
      internetProviders,
      internetTypes,
      internetAges,
      routerAges
    }
  } = props;

  const otherISP = get(find(internetProviders, {subElement: 'Other'}), 'id');

  return (
    <div className='grid form-contents large-floating-text'>
      <div className='row'>
        <div className='col'>
          <DropDownMenu
            onChange={onChange('internetProviderId')}
            {...renderMenuAttributes(survey, 'internetProviderId', internetProviders, fieldLabels, errors)}
          />
        </div>
        <div className='col'>
          <CustomTextField
            disabled={!(survey.internetProviderId === otherISP)}
            {...renderTextAttributes(survey, 'internetProviderOther', fieldLabels, errors, onChange)}
          />
        </div>
        <div className='col'>
          <DropDownMenu
            onChange={onChange('internetTypeId')}
            {...renderMenuAttributes(survey, 'internetTypeId', internetTypes, fieldLabels, errors)}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col input-container internet-speeds'>
          <SubText
            {...renderModalAttributes('internetSpeeds', fieldLabels, onModal)}
          >
            <CustomTextField
              min={0}
              {...renderTextAttributes(survey, 'internetDownloadSpeed', fieldLabels, errors, onChange)}
            />
          </SubText>
        </div>
        <div className='col input-container internet-speeds'>
          <SubText
            {...renderModalAttributes('internetSpeeds', fieldLabels, onModal)}
          >
            <CustomTextField
              min={0}
              {...renderTextAttributes(survey, 'internetUploadSpeed', fieldLabels, errors, onChange)}
            />
          </SubText>
        </div>
        <div className='col'>
          <DropDownMenu
            onChange={onChange('internetAgeId')}
            {...renderMenuAttributes(survey, 'internetAgeId', internetAges, fieldLabels, errors)}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col'>
          <ToggleButton
            onChange={onChange('hasRouter')}
            {...renderMenuAttributes(survey, 'hasRouter', YesNo, fieldLabels, errors)}
          />
        </div>
        <div className={cx('col', {'smaller-label': true})}>
          <ToggleButton
            onChange={onChange('isRouterLocatedInSameArea')}
            {...renderMenuAttributes(survey, 'isRouterLocatedInSameArea', YesNo, fieldLabels, errors)}
          />
        </div>
        <div className={cx('col', {'smaller-label': true})}>
          <ToggleButton
            onChange={onChange('supportsVOIP')}
            {...renderMenuAttributes(survey, 'supportsVOIP', YesNo, fieldLabels, errors)}
          />
        </div>
      </div>
    </div>
  );

}

InternetSection.propTypes = {
  onChange: PropTypes.func.isRequired,
  onModal: PropTypes.func,
  survey: PropTypes.object.isRequired,
  staticData: PropTypes.object.isRequired,
  fieldLabels: PropTypes.object.isRequired,
  errors: PropTypes.object
};

function SmartPhoneSection(props) {

  const {
    survey,
    errors,
    onChange,
    fieldLabels
  } = props;

  return (
    <div className='grid form-contents large-floating-text'>
      <div className='row'>
        <div className='col'>
          <CustomTextField
            {...renderTextAttributes(survey, 'smartPhoneMakeAndModel', fieldLabels, errors, onChange)}
          />
        </div>
        <div className='col'>
          <ToggleButton
            onChange={onChange('hotspot')}
            {...renderMenuAttributes(survey, 'hotspot', YesNo, fieldLabels, errors)}
          />
        </div>
      </div>
    </div>
  );
}

SmartPhoneSection.propTypes = {
  onChange: PropTypes.func.isRequired,
  onModal: PropTypes.func,
  survey: PropTypes.object.isRequired,
  staticData: PropTypes.object.isRequired,
  fieldLabels: PropTypes.object.isRequired,
  errors: PropTypes.object
};

export default function TechnologySurveyForm(props) {

  const onChange = (field) =>
    (event, value) => {
      if (props.onChange) {
        props.onChange(field, value);
      }
    }
  ;

  const onSave = () => {
    if (props.onSave) {
      props.onSave();
    }
  };

  const onSubmit = () => {
    if (props.onSubmit) {
      props.onSubmit();
    }
  };

  const onModal = (note) => {
    if (props.onModal) {
      props.onModal(note);
    }
  };

  return (

    <div
      className={cx('application-section-container applicant-technology-survey-container clearfix', {admin: props.adminView})}>
      {
        renderModal(props.modalMessage, onModal)
      }
      {
        renderTitleText(props.fieldLabels)
      }
      {
        renderInstructionText(props)
      }
      <div className='form-subheading' data-id='Your Computer'>
        Your Computer
      </div>
      <ComputerSection
        survey={props.survey}
        staticData={props.staticData}
        fieldLabels={props.fieldLabels}
        errors={props.errors}
        onChange={onChange}
        onModal={onModal}
      />
      <div className='form-subheading' data-id='Other Equipment'>
        Other Equipment
      </div>
      <EquipmentSection
        survey={props.survey}
        staticData={props.staticData}
        fieldLabels={props.fieldLabels}
        errors={props.errors}
        onChange={onChange}
      />
      <div className='form-subheading' data-id='Your Software'>
        Your Software
      </div>
      <SoftwareSection
        survey={props.survey}
        staticData={props.staticData}
        fieldLabels={props.fieldLabels}
        errors={props.errors}
        onChange={onChange}
        onModal={onModal}
      />
      <div className='form-subheading' data-id='Your Internet Connection'>
        Your Internet Connection
      </div>
      <InternetSection
        survey={props.survey}
        staticData={props.staticData}
        fieldLabels={props.fieldLabels}
        errors={props.errors}
        onChange={onChange}
        onModal={onModal}
      />
      <div className='form-subheading' data-id='Your Internet Connection'>
        Your Cell Phone
      </div>
      <SmartPhoneSection
        survey={props.survey}
        staticData={props.staticData}
        fieldLabels={props.fieldLabels}
        errors={props.errors}
        onChange={onChange}
        onModal={onModal}
      />

      <div className='right-container'>
        {
          renderErrorText(props.errors)
        }
        <div
          className={'text-btn back noTextSelect'}
          data-id='Save'
          onClick={onSave}>
          Save
        </div>
        {
          renderCompleteButton(props, onSubmit)
        }
      </div>

    </div>

  );

}

TechnologySurveyForm.propTypes = {
  survey: PropTypes.object.isRequired,
  staticData: PropTypes.object.isRequired,
  fieldLabels: PropTypes.object.isRequired,
  errors: PropTypes.object,
  modalMessage: PropTypes.string,
  adminView: PropTypes.bool,
  allowSubmit: PropTypes.bool,
  onModal: PropTypes.func,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
  onSubmit: PropTypes.func
};




// WEBPACK FOOTER //
// ./shared/react/applicant/TechnologySurvey/TechnologySurveyForm.js